import {useCallback, useEffect, useRef, useState} from 'react';

interface UseAudioPlaybackReturn {
  isPlaying: boolean;
  playbackProgress: number;
  seekTime: number;
  play(src: string): void;
  pause(): void;
  togglePlayPause(src: string): void;
}

const pauseListeners = new Set<() => void>();

function notifyOthersToPause(currentPause: () => void): void {
  for (const listener of pauseListeners) {
    if (listener !== currentPause) {
      listener();
    }
  }
}

export function useAudioPlayback(): UseAudioPlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [seekTime, setSeekTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentSrcRef = useRef<string | undefined>(undefined);
  const rafIdRef = useRef<number | null>(null);

  const stopProgressLoop = useCallback((): void => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const startProgressLoop = useCallback((): void => {
    const tick = (): void => {
      if (audioRef.current?.duration) {
        setSeekTime(audioRef.current.currentTime);
        setPlaybackProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
      }
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
  }, []);

  const pause = useCallback((): void => {
    stopProgressLoop();
    setIsPlaying(false);
    setPlaybackProgress(0);
    setSeekTime(0);
    currentSrcRef.current = undefined;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', pause);
      audioRef.current = null;
    }
  }, [stopProgressLoop]);

  const play = useCallback((src: string): void => {
    notifyOthersToPause(pause);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeEventListener('ended', pause);
    }
    stopProgressLoop();

    setIsPlaying(true);
    setPlaybackProgress(0);
    setSeekTime(0);
    currentSrcRef.current = src;

    const audio = new Audio(src);
    audio.addEventListener('ended', pause);

    audio.play().then((): void => {
      if (audioRef.current !== audio) {
        audio.pause();
        return;
      }
      startProgressLoop();
    }).catch((e: unknown) => {
      console.error(e);
      if (audioRef.current === audio) {
        pause();
      }
    });

    audioRef.current = audio;
  }, [pause, stopProgressLoop, startProgressLoop]);

  const togglePlayPause = useCallback((src: string): void => {
    if (isPlaying && currentSrcRef.current === src) {
      pause();
    } else {
      play(src);
    }
  }, [isPlaying, pause, play]);

  useEffect(() => {
    pauseListeners.add(pause);
    return (): void => {
      pauseListeners.delete(pause);
      stopProgressLoop();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeEventListener('ended', pause);
        audioRef.current = null;
      }
    };
  }, [pause, stopProgressLoop]);

  return {
    isPlaying,
    playbackProgress,
    seekTime,
    play,
    pause,
    togglePlayPause,
  };
}
